import { types as t, flow, getSnapshot } from '@invisr/mobx-state-tree'
import { observable, action, makeObservable, runInAction } from 'mobx'
import crudActions from '../crud-actions'
import errorToast from '../../../utils/error-toast'
import JS from '../javascript'
import Mapping from '../mapping'
import ServerFunctionsLog from '../ServerFunctionsLog'
import { JSProvider } from '../javascript'
import secretsActions from '../secrets-actions'
import { APP_ENVIRONMENT, NOTIFICATIONS_WS_SERVER_HOST, NOTIFICATIONS_WS_SERVER_PORT } from '../../../../env-public'

const PROVIDER_TYPE = 'Notifications';
const isLocal = APP_ENVIRONMENT === 'local'

export class NotificationsProvider {
  data = []
  connectedCount
  config
  client
  jsProvider
  isLoading = false
  
  constructor(config) {
    makeObservable(this, {
      data: observable,
      setData: action
    })

    this.config = config;

    this.updateData = this.updateData.bind(this);
    this._runFn = this._runFn.bind(this);
  }

  query = async () => {
    if (this.client) {
      await this.client.dispose();
    }
    const inputParams = await this.config.context.mapper.convertProps()
    const NotificationsClient = (await import('./client')).default
    const on = {}
    if (this.config.onClientConnect?.js) {
      on.connected = (_socket, payload) => {
        if (payload?.connectedCount) {
          this.connectedCount = payload.connectedCount;
        }
        this._runFn('onClientConnect', payload)
      };
    }
    if (this.config.onClientDisconnect?.js) {
      on.closed = () => {
        const payload = this.connectedCount ? { connectedCount: this.connectedCount } : { connectedCount: 0 }
        this._runFn('onClientDisconnect', payload)
      };
    }
    this.client = new NotificationsClient({
      url: `${isLocal ? 'ws' : 'wss'}://${NOTIFICATIONS_WS_SERVER_HOST}:${NOTIFICATIONS_WS_SERVER_PORT}/notifications`,
      on,
      connectionParams: () => ({
        dataSourceKey: this.config.dbKey,
        inputParams
      })
    });

    this.client.notifications.subscribe({
      next: ({ data: { userNotifications: { body } } }) => {
        const parsedBody = body ? JSON.parse(body) : body;

        if (parsedBody.$system) {
          if (parsedBody.connectedCount) {
            this.connectedCount = parsedBody.connectedCount;
          }
          return
        }

        this.updateData(parsedBody);

        if (this.config.onMessage?.js) {
          this._runFn('onMessage', parsedBody);
        }
      },
      error: err => {
        errorToast('Error while creating data source: ', err)
      }
    });

    return this.data;
  }

  updateData(newData) {
    runInAction(() => {
      this.data.unshift(newData);
    })
  }

  setData(data) {
    this.data = data;
  }

  clear() {
    if (this.data.length) {
      this.data.clear()
    }
  }

  async _runFn(fnName, message) {
    if (!this.config?.[fnName]) return
    if (!this.jsProvider) this.jsProvider = {}
    if (!this.jsProvider[fnName]) this.jsProvider[fnName] = new JSProvider(this.config[fnName]);

    try {
      return await this.jsProvider[fnName].run(...[message]);
    } catch (e) {
      console.error(e)
    }
  }
}

const secureKeys = ['onConnect', 'onDisconnect', 'webhookHandler']

const defaultFunctionConfigurationMappings = {
  webhookHandler: {
    inputs: { type: 'object', children: [{ type: 'object', key: 'request' }] }
  },

  onConnect: {
    inputs: { type: 'object', children: [{ type: 'string', key: 'webhookUrl' }] }
  },

  onDisconnect: {
    inputs: { type: 'object', children: [{ type: 'object', key: 'subscription' }] }
  },

  onClientConnect: {
    inputs: { type: 'object', children: [{ type: 'object', key: 'connectionInfo' }] },
    context: { type: 'object', children: [] }
  },

  onClientDisconnect: {
    inputs: { type: 'object', children: [{ type: 'object', key: 'connectionInfo' }] },
    context: { type: 'object', children: [] }
  },
}

const NotificationsConfig = t
  .model(PROVIDER_TYPE, {
    type: t.optional(t.string, PROVIDER_TYPE),
    context: t.maybeNull(t.late(() => Mapping)),
    onConnect: t.optional(JS, {}),
    onDisconnect: t.optional(JS, {}),
    onClientConnect: t.maybeNull(JS),
    onClientDisconnect: t.maybeNull(JS),
    webhookHandler: t.optional(JS, {}),
    onMessage: t.optional(JS, {})
  })
  .views(self => ({
    get provider() {
      return NotificationsProvider
    },
    get mapping () {
      const m = Mapping.create({ type: 'object' }, { ro: true, parentDataSource: self })
      m.children = [self.context, self.onMessage.context, self.onClientConnect.context, self.onClientDisconnect.context].filter(Boolean)
      return m
    },
    get logProvider() {
      return ServerFunctionsLog
    }
  }))
  .views(self => ({
    labels() {
      return {
        key: self.id,
        label: self.name
      }
    }
  }))
  .actions(secretsActions)
  .actions(self => ({
    load: flow(function* () {
      self.setIsOperationInProgress(true);
      const cfg = yield self.fetchSecureConfig(self.dbKey);
      self.setIsOperationInProgress(false);
      
      for (const k of secureKeys) {
        self[k] = cfg[k] || { ...defaultFunctionConfigurationMappings[k] }
      }
    }),
    afterCreate () {
      if (!self.context) {
        self.update('context', {
          type: 'object',
          children: []
        })
      }

      if (self.onMessage && (!self.onMessage.inputs.children?.length || !self.onMessage.context)) {
        self.onMessage.update({
          inputs: { type: 'object', children: [ { type: 'string', key: 'message' } ] },
          context: { type: 'object', children: [] }
        })
      }
      if (!self.onClientConnect) {
        self.update('onClientConnect', defaultFunctionConfigurationMappings.onClientConnect)
      }
      if (!self.onClientDisconnect) {
        self.update('onClientDisconnect', defaultFunctionConfigurationMappings.onClientDisconnect)
      }
     },
    onClone: flow(function* (newDbKey) {
      const secretFields = self.getSecretFields(self.context.children)?.filter(f => f.value);
      const data = {
        provider: PROVIDER_TYPE,
        sourceCredentialsDbKey: self.dbKey,
        targetCredentialsDbKey: newDbKey,
        secrets: {
          fields: secretFields
        }
      };
      yield self.cloneSecretData(data);
    }),
    afterSave: flow(function*(ds, tempDS) {
      const secretFields = tempDS.getSecretFields(tempDS.context.children)?.filter(f => f.value);
      tempDS.clearSecretFields(tempDS.context.children);

      const data = {
        provider: PROVIDER_TYPE,
        dbKey: ds.dbKey,
        secrets: {
          fields: secretFields,
          config: {
            ...secureKeys.reduce((o, k) => ({ ...o, [k]: tempDS[k] }), {}),
            context: filterSecureContext(getSnapshot(tempDS.context))
          }
        }
      };
      
      yield self.saveSecretData(data);
    })}))
  .actions(crudActions);

function filterSecureContext(context) {
  return {
    ...context,
    children: context.children.reduce((children, child) => {
      if (child.children) {
        children.push(filterSecureContext(child))
      } else if (child.bindingType && child.type === 'secret') {
          children.push({ ...child })
      }

      return children;
    }, [])
  };
}

export default NotificationsConfig.postProcessSnapshot(snap => {
  let s = { ...snap }
  for (const k of secureKeys) {
    delete s[k]
  }

  return s
})
